import React, {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { CircularProgress } from '@mui/material';
import { assertUnreachable } from '@shared/helpers/function.helpers';
import { REPORT_THUMBNAIL_INSET, useReportStyles } from '../../report.styles';
import { useGetScalingSize } from '../../report-style.helpers';
import {
  getVisibleReportPageKeys,
  type ReportPageItem,
} from '../../report-visibility.helpers';
import { IconButton } from '@ui/buttons';
import { Tooltip } from '@ui/Tooltip';
import { useReportPageThumbnails } from '../../hooks/use-report-page-thumbnails.hook';
import {
  getReportThumbnailDisplay,
  type ReportThumbnailDisplay,
} from '../../helpers/report-thumbnail-display.helpers';

type ThumbnailSlotClasses = {
  reportThumbnailImage: string;
  reportThumbnailLoader: string;
};

/** Image, spinner, or empty box for one thumbnail rail slot. */
const renderThumbnailSlot = (
  display: ReportThumbnailDisplay,
  classes: ThumbnailSlotClasses,
): ReactNode => {
  switch (display.kind) {
    case 'hidden':
    case 'empty':
      return null;
    case 'pending':
      return (
        <CircularProgress
          size={18}
          color="inherit"
          className={classes.reportThumbnailLoader}
        />
      );
    case 'ready':
      return (
        <img
          alt=""
          draggable={false}
          src={display.url}
          className={classes.reportThumbnailImage}
        />
      );
    default:
      return assertUnreachable(display);
  }
};

const PANEL_DEFAULT_WIDTH = 184;
const PANEL_MIN_WIDTH = 140;
const PANEL_MAX_WIDTH = 360;

/**
 * Scrollable report with a thumbnail rail. Visible pages mount once in the
 * main pane; the rail shows PNG snapshots of those pages (not a second React
 * tree). Hidden pages stay in the list as dimmed empty slots until unskipped.
 */
export const ReportDefaultWrapper: FC<{
  /** Visible pages, rendered in the main pane. */
  children: ReactNode[];
  toolbar?: ReactNode;
  /** Every page — hidden ones included — in thumbnail order. */
  pageItems: ReportPageItem[];
  /** Omitted when the report has no show/hide control. */
  onTogglePageHidden?: (pageKey: string, nextHidden: boolean) => void;
  thumbnailContentKey?: string;
}> = ({
  children,
  toolbar,
  pageItems,
  onTogglePageHidden,
  thumbnailContentKey = '',
}) => {
  const getScalingSize = useGetScalingSize();
  const { classes } = useReportStyles({ getScalingSize });

  const [panelWidth, setPanelWidth] = useState(PANEL_DEFAULT_WIDTH);
  const panelWidthRef = useRef(PANEL_DEFAULT_WIDTH);
  const mainRef = useRef<HTMLDivElement>(null);

  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = panelWidthRef.current;
    const onMove = (mv: MouseEvent) => {
      const next = Math.max(
        PANEL_MIN_WIDTH,
        Math.min(PANEL_MAX_WIDTH, startWidth + mv.clientX - startX),
      );
      panelWidthRef.current = next;
      setPanelWidth(next);
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, []);

  /**
   * Scrolls the main pane to a page thumbnail index.
   * Targets only direct scroll anchors — {@link ReportPage} also sets
   * `data-report-page`, which would double the node list and break indices.
   * A skipped thumbnail that sorts after every visible page has no anchor of
   * its own; scrolling to the end puts the viewport where it would appear.
   */
  const scrollToPage = useCallback((index: number) => {
    const container = mainRef.current;
    if (!container) return;

    const pages = container.querySelectorAll<HTMLElement>(
      ':scope > [data-report-scroll-anchor]',
    );
    if (!pages.length) return;
    const page = pages[index];

    const maxScroll = Math.max(
      0,
      container.scrollHeight - container.clientHeight,
    );
    const pageTop = page
      ? container.scrollTop +
        page.getBoundingClientRect().top -
        container.getBoundingClientRect().top
      : maxScroll;

    container.scrollTo({
      top: Math.min(maxScroll, Math.max(0, pageTop)),
      behavior: 'smooth',
    });
  }, []);

  // Unskipping puts the page back on the canvas — follow it there, once the
  // re-render has produced its scroll anchor.
  const pendingScrollKey = useRef<string | null>(null);
  useEffect(() => {
    const item = pageItems.find(({ key }) => key === pendingScrollKey.current);
    if (!item || item.isHidden || item.visibleIndex === null) return;
    pendingScrollKey.current = null;
    scrollToPage(item.visibleIndex);
  }, [pageItems, scrollToPage]);

  const togglePageHidden = useCallback(
    (pageKey: string, nextHidden: boolean) => {
      pendingScrollKey.current = nextHidden ? null : pageKey;
      onTogglePageHidden?.(pageKey, nextHidden);
    },
    [onTogglePageHidden],
  );

  const thumbWidth = panelWidth - REPORT_THUMBNAIL_INSET * 2;
  const visiblePageKeys = useMemo(
    () => getVisibleReportPageKeys(pageItems),
    [pageItems],
  );
  const thumbnailUrls = useReportPageThumbnails({
    mainRef,
    pageKeys: visiblePageKeys,
    thumbWidthPx: thumbWidth,
    contentKey: thumbnailContentKey,
  });

  return (
    <div className={classes.reportDefaultWrapper}>
      <div className={classes.reportPanel} style={{ width: panelWidth }}>
        {toolbar && <div className={classes.reportPanelToolbar}>{toolbar}</div>}
        <div className={classes.reportPanelThumbnails}>
          {pageItems.map(({ key, isHidden, scrollIndex, visibleIndex }) => (
            <div
              key={key}
              data-hidden={isHidden || undefined}
              className={classes.reportThumbnailWrapper}
              onClick={() => scrollToPage(scrollIndex)}
            >
              <div data-thumbnail className={classes.reportThumbnail}>
                {renderThumbnailSlot(
                  getReportThumbnailDisplay(
                    visibleIndex,
                    visibleIndex === null
                      ? undefined
                      : thumbnailUrls[visibleIndex],
                  ),
                  classes,
                )}
              </div>
              {onTogglePageHidden && (
                <div
                  data-visibility-toggle
                  className={classes.reportThumbnailVisibility}
                >
                  <Tooltip title={isHidden ? 'Unskip slide' : 'Skip slide'}>
                    <IconButton
                      category="mui"
                      icon={isHidden ? 'VisibilityOff' : 'Visibility'}
                      size="medium"
                      shape="square"
                      aria-label={isHidden ? 'Unskip slide' : 'Skip slide'}
                      onClick={(event) => {
                        event.stopPropagation();
                        togglePageHidden(key, !isHidden);
                      }}
                    />
                  </Tooltip>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div
        className={classes.reportResizeHandle}
        onMouseDown={handleResizeStart}
      />

      <div ref={mainRef} className={classes.reportDefaultWrapperMain}>
        {children.map((page, index) => (
          <div
            key={visiblePageKeys[index] ?? index}
            data-report-scroll-anchor={index}
          >
            {page}
          </div>
        ))}
      </div>
    </div>
  );
};
